import moment from 'moment-timezone';

/**
 * Filters events based on America/New_York timezone
 * Separates events into past and future/upcoming events
 * 
 * @param {Array} events - Array of event objects with event_date, event_time properties
 * @param {String} timezone - Timezone to use for comparison (default: 'America/New_York')
 * @returns {Object} Object containing { pastEvents: [], upcomingEvents: [] }
 */
export const filterEventsByTimezone = (events = [], timezone = 'America/New_York') => {
  if (!Array.isArray(events) || events.length === 0) {
    return { pastEvents: [], upcomingEvents: [] };
  }

  const now = moment().tz(timezone);

  const pastEvents = [];
  const upcomingEvents = [];

  events.forEach((event) => {
    try {
      // Parse event date and time in the specified timezone
      const eventDateTime = moment.tz(
        `${event.event_date} ${event.event_time}`,
        'YYYY-MM-DD HH:mm:ss',
        timezone
      );

      // Check if the event is valid
      if (!eventDateTime.isValid()) {
        console.warn(`Invalid event date/time for event ${event.id}:`, event.event_date, event.event_time);
        return;
      }

      // Compare with current time
      if (eventDateTime.isBefore(now)) {
        pastEvents.push(event);
      } else {
        upcomingEvents.push(event);
      }
    } catch (error) {
      console.error(`Error filtering event ${event.id}:`, error);
    }
  });

  // Sort past events in descending order (most recent first)
  pastEvents.sort((a, b) => {
    const timeA = moment.tz(`${b.event_date} ${b.event_time}`, 'YYYY-MM-DD HH:mm:ss', timezone);
    const timeB = moment.tz(`${a.event_date} ${a.event_time}`, 'YYYY-MM-DD HH:mm:ss', timezone);
    return timeA.diff(timeB);
  });

  // Sort upcoming events in ascending order (soonest first)
  upcomingEvents.sort((a, b) => {
    const timeA = moment.tz(`${a.event_date} ${a.event_time}`, 'YYYY-MM-DD HH:mm:ss', timezone);
    const timeB = moment.tz(`${b.event_date} ${b.event_time}`, 'YYYY-MM-DD HH:mm:ss', timezone);
    return timeA.diff(timeB);
  });

  return { pastEvents, upcomingEvents };
};

/**
 * Checks if a single event has passed based on America/New_York timezone
 * 
 * @param {Object} event - Event object with event_date, event_time properties
 * @param {String} timezone - Timezone to use for comparison (default: 'America/New_York')
 * @returns {Boolean} true if event has passed, false otherwise
 */
export const isEventPassed = (event, timezone = 'America/New_York') => {
  try {
    const eventDateTime = moment.tz(
      `${event.event_date} ${event.event_time}`,
      'YYYY-MM-DD HH:mm:ss',
      timezone
    );

    const now = moment().tz(timezone);

    return eventDateTime.isBefore(now);
  } catch (error) {
    console.error('Error checking if event passed:', error);
    return false;
  }
};

/**
 * Gets the current time in America/New_York timezone
 * 
 * @returns {moment.Moment} Current time in America/New_York timezone
 */
export const getCurrentTimeInTimezone = (timezone = 'America/New_York') => {
  return moment().tz(timezone);
};

/**
 * Converts an event's date and time to America/New_York timezone
 * 
 * @param {String} event_date - Event date in YYYY-MM-DD format
 * @param {String} event_time - Event time in HH:mm:ss format
 * @param {String} timezone - Timezone to use (default: 'America/New_York')
 * @returns {moment.Moment} Event time in the specified timezone
 */
export const convertEventToTimezone = (event_date, event_time, timezone = 'America/New_York') => {
  try {
    return moment.tz(
      `${event_date} ${event_time}`,
      'YYYY-MM-DD HH:mm:ss',
      timezone
    );
  } catch (error) {
    console.error('Error converting event time:', error);
    return null;
  }
};

/**
 * Checks if an event is joinable (within 5 minutes before start in America/New_York timezone)
 * Room becomes available 5 minutes before event time
 * 
 * @param {Object} event - Event object with event_date, event_time properties
 * @param {Number} minutesBefore - Minutes before event time when room becomes available (default: 5)
 * @param {String} timezone - Timezone to use for comparison (default: 'America/New_York')
 * @returns {Boolean} true if event can be joined (within the 5-minute window)
 */
export const isEventJoinable = (event, minutesBefore = 5, timezone = 'America/New_York') => {
  try {
    if (!event?.event_date || !event?.event_time) {
      return false;
    }

    const eventDateTime = moment.tz(
      `${event.event_date} ${event.event_time}`,
      'YYYY-MM-DD HH:mm:ss',
      timezone
    );

    if (!eventDateTime.isValid()) {
      return false;
    }

    const now = moment().tz(timezone);
    const joinableTime = eventDateTime.clone().subtract(minutesBefore, 'minutes');

    // Event is joinable if current time is between (event_time - 5 minutes) and event_time
    // and the event hasn't passed yet
    return now.isSameOrAfter(joinableTime) && now.isBefore(eventDateTime);
  } catch (error) {
    console.error('Error checking if event is joinable:', error);
    return false;
  }
};
